import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { ManageConnectionService } from '../../services/manage-connection.service';
import { Auth0Client, Connection } from '../../models/connection';
import { forkJoin, switchMap } from 'rxjs';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { GISDashboard } from '../../models/gis-group';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { Router } from '@angular/router';
import { UserInvitation } from '../../models/user-invitation';
import { ManageUsersService } from '../../../manage-users/services/manage-users.service';
import { CRMService } from '../../../../shared/services/CRM/crm.service';
import { CRMRoles } from '../../models/crm-roles';
import { SettingsService } from '../../../../shared/services/settings/settings.service';
import { settingsTypes } from '../../../../shared/models/settings';
import { EulaItem, EulaType } from '../../../license-agreement/models/eula';
import { environment } from '../../../../../environments/environment';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { ContainerSasUrl } from '../../../../shared/interfaces/company';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import { LoggedInUser } from '../../../../shared/models/logged-in-user';
import { AuthService } from '../../../auth/services/auth.service';
import { SubscriptionService } from '../../../subscription/services/subscription.service';
import { ConnectionSubscriptionDetails } from '../../../subscription/models/subscription';

interface FileOperationSetting {
  operation: string;
  settings?: Record<string, unknown>;
}

@Component({
  selector: 'app-connection-details',
  standalone: true,
  imports: [
    FormsModule,
    PopupComponent,
    DatePipe,
    ClickOutsideDirective,
    NgClass,
  ],
  templateUrl: './connection-details.component.html',
  styleUrls: ['./connection-details.component.scss'],
})
export class ConnectionDetailsComponent extends DestroyBase implements OnInit {
  @Input({ required: true }) connection!: Connection;
  @Output() connectionDeletedEventEmitter = new EventEmitter<Connection>();

  router = inject(Router);
  connectionService = inject(ManageConnectionService);
  userService = inject(ManageUsersService);
  subscriptionService = inject(SubscriptionService);
  crmService = inject(CRMService);
  settingsService = inject(SettingsService);
  auditLogService = inject(AuditLogService);
  private commonService = inject(CommonService);
  private imageService = inject(ImageService);
  private authService = inject(AuthService);

  allClients: Auth0Client[] = [];
  connectionClientMap: Record<string, boolean> = {};
  isClientsExpanded = false;
  connectionClientsEnabled!: number;
  subscriptionDetails: ConnectionSubscriptionDetails | null = null;
  isSubscriptionExpanded = false;
  subscriptionLoading = true;
  arcGisDashboardList: GISDashboard[] = [];
  allArcGisDashboards: GISDashboard[] = [];
  connectionArcGisGroupMap: Record<string, boolean> = {};
  isArcGisGroupsExpanded = false;
  connectionArcGisGroupEnabled!: number;

  // EULA section properties
  eulaList: EulaItem[] = [];
  allEulaItems: EulaItem[] = [];
  private _selectedEulaType: EulaType = EulaType.Default;
  isEulaExpanded = false;

  get selectedEulaType(): EulaType {
    return this._selectedEulaType;
  }

  set selectedEulaType(value: EulaType | string | number) {
    // Handle conversion from string/number to EulaType enum
    if (typeof value === 'string') {
      this._selectedEulaType = Number(value) as EulaType;
    } else if (typeof value === 'number') {
      this._selectedEulaType = value as EulaType;
    } else {
      this._selectedEulaType = value;
    }
  }
  isEULASettingExists = false;
  eulaLoading = false;
  strategies: Record<string, string> = {
    waad: 'Microsoft Azure AD',
    'google-apps': 'Google Workspace',
  };

  loading = false;
  showDeleteConfirmPopup = false;
  showUpdateConfirmPopup = false;

  // Invitation properties
  invitations: UserInvitation[] = [];
  inviteEmail = '';
  isInviting = false;
  showInviteUserPopup = false;
  showCancelInvitationPopup = false;
  invitationToDelete: UserInvitation | null = null;

  // CRM Roles properties
  allCrmRoles: CRMRoles[] = [];
  isCrmEnabled = false;
  selectedCrmRoleId: string | null = null;
  selectedCrmRole: CRMRoles | null = null;
  isCrmRoleReadonly = false;
  isCRMSettingExists = false;
  showCrmRoleWarningPopup = false;
  tempSelectedCrmRoleId: string | null = null;
  crmLoading = false;
  originalSelectedCrmRoleId: string | null = null; // Store original role for re-enabling
  showCRMIntegration = environment.showCRMIntegration;

  // EULA confirmation popup properties
  showEulaConfirmPopup = false;
  tempSelectedEulaType: EulaType | null = null;
  previousEulaType: EulaType | null = null;
  dropdownEulaType: EulaType = EulaType.Default; // Separate property for dropdown display

  // PDM (File Operation) section properties
  isPdmEnabled = false;
  isPdmSettingExists = false;
  isPdmExpanded = false;
  pdmLoading = false;
  showPdmConfirmPopup = false;

  // Copy link animation
  copiedInvitationId: string | null = null;

  logoFile: File | null = null;
  imageUrl: string | null = null;

  isDragOver = false;

  showLogoMenu = false;

  containerSasUrl!: ContainerSasUrl;

  loggedInUser: LoggedInUser | null = null;

  toggleLogoMenu(): void {
    this.showLogoMenu = !this.showLogoMenu;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;

    const file = event.dataTransfer?.files?.[0];

    if (!file) {
      return;
    }

    this.processLogoFile(file);
  }

  processLogoFile(file: File): void {
    const validExtensions = ['jpg', 'jpeg', 'png', 'svg'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const fileSizeMB = file.size / (1024 * 1024);

    if (!extension || !validExtensions.includes(extension)) {
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: 'Invalid file type. Please select JPG, JPEG, PNG or SVG.',
      });
      return;
    }

    if (fileSizeMB > 10) {
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: 'Logo file size exceeds 10MB.',
      });
      return;
    }

    this.logoFile = file;
    this.showLogoMenu = false;

    this.uploadLogo();
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    this.processLogoFile(input.files[0]);

    input.value = '';
  }

  uploadLogo(): void {
    if (!this.logoFile) {
      return;
    }
    this.imageService
      .uploadConnectionLogoByAdmin(this.connection.name, this.logoFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (logo) => {
          this.loadLogo(logo.url);
          this.logoFile = null;

          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Logo uploaded successfully.',
          });
        },
        error: (err) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: err.error?.detail ?? 'Failed to upload logo.',
          });
        },
      });
  }

  removeLogo(): void {
    this.imageService
      .deleteConnectionLogo(this.connection.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.logoFile = null;
          this.imageUrl = null;
          this.showLogoMenu = false;

          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Logo removed successfully.',
          });

          if (this.loggedInUser?.connection === this.connection.name) {
            this.imageService.connectionImageUrlSubject.next(null);
          }
        },
        error: (err) => {
          this.showLogoMenu = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: err.error?.detail ?? 'Failed to remove logo.',
          });
        },
      });
  }

  getLogo(): void {
    forkJoin({
      connectionLogo: this.imageService.getConnectionLogo(this.connection.name),
      sasUrl: this.imageService.getContainerSasUrl(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ connectionLogo, sasUrl }) => {
          this.containerSasUrl = sasUrl;

          if (connectionLogo?.url) {
            this.loadLogo(connectionLogo.url);
          } else {
            this.imageUrl = null;
          }
        },
        error: (error) => {
          this.imageUrl = null;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: error.error?.detail ?? 'Failed to fetch logo.',
          });
        },
      });
  }

  loadLogo(url: string | null): void {
    const cacheBuster = `t=${Date.now()}`;
    this.imageUrl = this.containerSasUrl
      ? `${url}?${this.containerSasUrl.sas_token}&${cacheBuster}`
      : `${url}?${cacheBuster}`;
    if (this.loggedInUser?.connection === this.connection.name) {
      this.imageService.connectionImageUrlSubject.next(this.imageUrl);
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.getLogo();
    this.loggedInUser = this.authService.getLoggedInUserInfo();
    this._loadSubscriptionDetails();
    this._loadConnectionClientMappings();
    if (this.showCRMIntegration) {
      this._loadCrmRoles();
    }
    this.ArcgisConfigurationAppExists();
    this._initializeEulaSection();
    this._loadEulaSettings();
    this._loadPdmSettings();

    // Load invitations if connection type is 2
    if (this.connection.connection_type === 2) {
      this._loadInvitations();
    }
  }

  saveConnection(): void {
    this.auditLogService.logFeature({
      featureName: 'connection_details',
      action: 'save',
      meta: {
        connectionName: this.connection.name,
        displayName: this.connection.display_name || '',
      },
      description: 'User saves connection details',
    });

    this.connectionService
      .updateConnection(this.connection)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._showToastMessage('Connection updated successfully', 'success');
          this.showUpdateConfirmPopup = false;
        },
        error: (error) => {
          this.showUpdateConfirmPopup = false;
          this._showToastMessage(error.error.message, 'error');
        },
      });
  }

  onHideToggleChange(): void {
    const isHidden = !!this.connection.hide;
    this.auditLogService.logFeature({
      featureName: 'connection_details',
      action: isHidden ? 'hide' : 'unhide',
      meta: {
        connectionName: this.connection.name,
        connectionId: String(this.connection.id),
      },
      description: isHidden
        ? 'User hides connection from public'
        : 'User shows connection to public',
    });

    this.connectionService
      .patchConnectionHide(this.connection.name, isHidden)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._showToastMessage(
            `Connection is now ${isHidden ? 'hidden from' : 'visible to'} public`,
            'success',
          );
        },
        error: (error) => {
          this.connection.hide = !isHidden;
          this._showToastMessage(
            error.error?.message ||
              error.error?.error ||
              'Failed to update connection hide status',
            'error',
          );
        },
      });
  }

  deleteConnection(): void {
    this.auditLogService.logFeature({
      featureName: 'connection',
      action: 'delete',
      meta: {
        connectionId: String(this.connection.id),
        connectionName: this.connection.name,
        displayName: this.connection.display_name || '',
      },
      description: 'User deletes a connection',
    });

    this.connectionService
      .deleteConnection(this.connection.id, this.connection.connection_type)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.connectionDeletedEventEmitter.emit(this.connection);
      });
  }

  private _loadSubscriptionDetails(): void {
    this.subscriptionLoading = true;
    this.subscriptionService
      .getConnectionSubscriptionDetails(this.connection.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details) => {
          this.subscriptionDetails = details;
          this.subscriptionLoading = false;
        },
        error: () => {
          this.subscriptionDetails = null;
          this.subscriptionLoading = false;
        },
      });
  }

  private _loadConnectionClientMappings(): void {
    this.connectionService
      .getClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.allClients = res;
        this.loading = false;
        this._mapClientsToConnection();
      });
  }

  private _mapClientsToConnection(): void {
    this.allClients.forEach((client) => {
      this.connectionClientMap[client.client_id] =
        this.connection.enabled_clients?.some((c) => c === client.client_id);
    });
    this.connectionClientsEnabled = Object.values(
      this.connectionClientMap,
    ).filter((value) => value === true).length;
  }

  toggleSubscription(): void {
    this.isSubscriptionExpanded = !this.isSubscriptionExpanded;
  }

  get subscriptionAdminName(): string | null {
    return this.subscriptionDetails?.subscription_admin_name?.trim() || null;
  }

  get subscriptionAdminEmail(): string | null {
    return this.subscriptionDetails?.subscription_admin_email?.trim() || null;
  }

  navigateToPlan(planName?: string): void {
    if (planName && planName !== 'free_plan') {
      let returnUrl = window.location.pathname + window.location.search;
      if (this.connection?.name && !returnUrl.includes('connection=')) {
        const separator = returnUrl.includes('?') ? '&' : '?';
        returnUrl = `${returnUrl}${separator}connection=${encodeURIComponent(this.connection.name)}`;
      }
      this.router.navigate(
        ['/configs/manage-subscription-plans/view', planName],
        {
          state: {
            returnUrl,
          },
        },
      );
    }
  }

  getStatusClass(status?: string): string {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'trialing':
        return 'status-trialing';
      case 'canceled':
        return 'status-canceled';
      case 'past_due':
        return 'status-past-due';
      default:
        return 'status-free';
    }
  }

  onConnectionClientChange(clientId: string, isEnabled: boolean): void {
    const clientName =
      this.allClients.find((c) => c.client_id === clientId)?.name || '';

    this.auditLogService.logFeature({
      featureName: 'client_settings',
      action: isEnabled ? 'enable' : 'disable',
      meta: {
        connectionName: this.connection.name,
        clientId: clientId,
        clientName: clientName,
      },
      description: isEnabled
        ? 'User enables client for connection'
        : 'User disables client for connection',
    });

    this.connection.enabled_clients = Object.entries(this.connectionClientMap)
      .filter(([, isEnabled]) => isEnabled)
      .map(([clientId]) => clientId);
    this.connectionService
      .mapConnectionClient(this.connection)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          this.showUpdateConfirmPopup = false;
          this._showToastMessage(error.error.message, 'error');
        },
      });
  }

  private _showToastMessage(
    message: string,
    type: 'success' | 'error' = 'error',
  ) {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message: message,
    });
  }

  onConnectionArcGisGroupChange(groupType: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const dashboardName =
      this.arcGisDashboardList.find((d) => d.type === groupType)?.title ||
      groupType;

    this.auditLogService.logFeature({
      featureName: 'arcgis_dashboard',
      action: isChecked ? 'enable' : 'disable',
      meta: {
        connectionName: this.connection.name,
        dashboardType: groupType,
        dashboardName: dashboardName,
      },
      description: isChecked
        ? 'User enables ArcGIS dashboard'
        : 'User disables ArcGIS dashboard',
    });

    if (isChecked) {
      this.connectionService
        .updateConnectionArcGISGroupMapping(this.connection.name, groupType)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.connectionArcGisGroupEnabled++);
    } else {
      this.connectionService
        .deleteConnectionArcGISGroupMapping(this.connection.name, groupType)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.connectionArcGisGroupEnabled--);
    }
  }

  toggleArcGisGroups() {
    this.isArcGisGroupsExpanded = !this.isArcGisGroupsExpanded;
  }

  toggleEula() {
    this.isEulaExpanded = !this.isEulaExpanded;
  }

  toggleClients() {
    this.isClientsExpanded = !this.isClientsExpanded;
  }

  onManageConnectionUsers(): void {
    if (
      this.connection.name?.toLowerCase() === 'sme' ||
      this.connection.display_name?.toLowerCase() === 'sme'
    ) {
      this.router.navigate(['/manage-users'], {
        queryParams: { tab: 'sme' },
      });
    } else {
      this.router.navigate(['/manage-users'], {
        queryParams: { connection: this.connection.name },
      });
    }
  }

  private readonly DASHBOARD_LIST = [
    {
      title: 'Site toolkit',
      selected: false,
      type: 'siteToolkit',
    },
    {
      title: 'GBRA',
      selected: false,
      type: 'gbra',
    },
  ];

  private readonly EULA_LIST: EulaItem[] = [
    {
      title: 'Default',
      selected: false,
      type: EulaType.Default,
    },
    {
      title: 'DeepRoots',
      selected: false,
      type: EulaType.DeepRoots,
    },
    {
      title: 'Free Trial',
      selected: false,
      type: EulaType.FreeTrial,
    },
  ];

  ArcgisConfigurationAppExists() {
    this.connectionService
      .getArcgisConfigurationAppExists(this.connection.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          type Dashboards = Record<string, boolean>;
          const dashboards: Dashboards = (res.dashboards ||
            {}) as unknown as Dashboards;

          this.arcGisDashboardList = this.DASHBOARD_LIST.map((item) => {
            const isSelected = dashboards[item.type] === true;
            this.connectionArcGisGroupMap[item.type] = isSelected;
            return { ...item, selected: isSelected };
          });

          this.connectionArcGisGroupEnabled =
            Object.values(dashboards).filter(Boolean).length;

          this.allArcGisDashboards = [...this.arcGisDashboardList];
        },
      });
  }

  private _initializeEulaSection(): void {
    // Initialize EULA section with default values
    this.eulaList = this.EULA_LIST.map((item) => ({
      ...item,
      selected: false,
    }));

    this.allEulaItems = [...this.eulaList];

    // Set default selection
    this.selectedEulaType = EulaType.Default;
    this.dropdownEulaType = EulaType.Default;
  }

  private _loadEulaSettings(): void {
    this.eulaLoading = true;
    this.settingsService
      .getSettingsForConnectionByType(this.connection.name, settingsTypes.EULA)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isEULASettingExists = true;
          const eulaSettings = res.settings_value as { type: string | number };

          if (eulaSettings.type) {
            const typeValue = Number(eulaSettings.type);
            this.selectedEulaType = typeValue as EulaType;
            this.dropdownEulaType = typeValue as EulaType;
          }
          this.eulaLoading = false;
        },
        error: (error) => {
          if (error.status === 404) {
            // No EULA settings found, keep default
            this.isEULASettingExists = false;
            this.selectedEulaType = EulaType.Default;
          } else {
            console.error('Failed to load EULA settings:', error);
            this._showToastMessage('Failed to load EULA settings', 'error');
          }
          this.eulaLoading = false;
        },
      });
  }

  onConnectionEulaChange(eulaType: EulaType | string | number): void {
    // Convert the input to EulaType enum (it comes as string from HTML)
    let convertedEulaType: EulaType;
    if (typeof eulaType === 'string') {
      convertedEulaType = Number(eulaType) as EulaType;
    } else if (typeof eulaType === 'number') {
      convertedEulaType = eulaType as EulaType;
    } else {
      convertedEulaType = eulaType;
    }

    // Store the current selection as previous
    this.previousEulaType = this.selectedEulaType;
    this.tempSelectedEulaType = convertedEulaType;

    // Update dropdown immediately to show user selection
    this.dropdownEulaType = convertedEulaType;

    // Don't update selectedEulaType yet - this will be done on confirmation
    // Show confirmation popup
    this.showEulaConfirmPopup = true;
  }

  confirmEulaChange(): void {
    if (this.tempSelectedEulaType === null) return;

    const eulaType = this.tempSelectedEulaType;
    this.selectedEulaType = eulaType;

    const eulaData = { type: eulaType.toString() };

    if (!this.isEULASettingExists) {
      this.settingsService
        .CreateSettingsForConnectionByType(
          this.connection.name,
          settingsTypes.EULA,
          eulaData,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isEULASettingExists = true;
            this._showToastMessage(
              'EULA settings created successfully',
              'success',
            );

            this.auditLogService.logFeature({
              featureName: 'eula_type',
              action: 'create',
              meta: {
                connectionName: this.connection.name,
                eulaType: String(eulaType),
              },
              description: 'User sets EULA type',
            });

            this.showEulaConfirmPopup = false;
            this.tempSelectedEulaType = null;
            this.previousEulaType = null;
          },
          error: (error) => {
            // Revert both selectedEulaType and dropdownEulaType on error
            this.selectedEulaType = this.previousEulaType!;
            this.dropdownEulaType = this.previousEulaType!;
            this._showToastMessage(
              error.error?.message || 'Failed to create EULA settings',
              'error',
            );
            this.showEulaConfirmPopup = false;
            this.tempSelectedEulaType = null;
            this.previousEulaType = null;
          },
        });
    } else {
      this.settingsService
        .updateSettingsForConnectionByType(
          this.connection.name,
          settingsTypes.EULA,
          eulaData,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this._showToastMessage(
              'EULA settings updated successfully',
              'success',
            );

            this.auditLogService.logFeature({
              featureName: 'eula_type',
              action: 'update',
              meta: {
                connectionName: this.connection.name,
                eulaType: String(eulaType),
              },
              description: 'User updates EULA type',
            });

            this.showEulaConfirmPopup = false;
            this.tempSelectedEulaType = null;
            this.previousEulaType = null;
          },
          error: (error) => {
            // Revert both selectedEulaType and dropdownEulaType on error
            this.selectedEulaType = this.previousEulaType!;
            this.dropdownEulaType = this.previousEulaType!;
            this._showToastMessage(
              error.error?.message || 'Failed to update EULA settings',
              'error',
            );
            this.showEulaConfirmPopup = false;
            this.tempSelectedEulaType = null;
            this.previousEulaType = null;
          },
        });
    }
  }

  cancelEulaChange(): void {
    // Revert dropdown to previous selection
    this.dropdownEulaType = this.previousEulaType!;

    // Close popup and clear temporary values
    this.showEulaConfirmPopup = false;
    this.tempSelectedEulaType = null;
    this.previousEulaType = null;
  }

  private _loadPdmSettings(): void {
    this.pdmLoading = true;
    this.settingsService
      .getSettingsForConnectionByType(
        this.connection.name,
        settingsTypes.FILE_OPERATION,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isPdmSettingExists = true;
          const val = res.settings_value;
          if (Array.isArray(val)) {
            this.isPdmEnabled = (val as FileOperationSetting[]).some(
              (op) => op && op.operation === 'copy_blob',
            );
          } else if (val && typeof val === 'object') {
            this.isPdmEnabled =
              (val as FileOperationSetting).operation === 'copy_blob';
          } else {
            this.isPdmEnabled = false;
          }
          this.pdmLoading = false;
        },
        error: (error) => {
          if (error.status === 404) {
            this.isPdmSettingExists = false;
            this.isPdmEnabled = false;
          } else {
            console.error('Failed to load PDM settings:', error);
            this._showToastMessage('Failed to load PDM settings', 'error');
          }
          this.pdmLoading = false;
        },
      });
  }

  onPdmToggleChange(): void {
    // Show confirmation popup before saving
    this.showPdmConfirmPopup = true;
  }

  confirmPdmChange(): void {
    this.showPdmConfirmPopup = false;

    const pdmPayload = {
      operation: 'copy_blob',
      settings: {
        destination_connection_string: '',
        destination_container_name: '',
        destination_file_name: '',
      },
    };

    if (this.isPdmEnabled) {
      // Enable: create or update
      if (!this.isPdmSettingExists) {
        this.settingsService
          .CreateSettingsForConnectionByType(
            this.connection.name,
            settingsTypes.FILE_OPERATION,
            pdmPayload,
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isPdmSettingExists = true;
              this._showToastMessage(
                'PDM file operation enabled successfully',
                'success',
              );
            },
            error: (error) => {
              this.isPdmEnabled = false;
              this._showToastMessage(
                error.error?.message || 'Failed to enable PDM',
                'error',
              );
            },
          });
      } else {
        this.settingsService
          .updateSettingsForConnectionByType(
            this.connection.name,
            settingsTypes.FILE_OPERATION,
            pdmPayload,
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this._showToastMessage(
                'PDM file operation updated successfully',
                'success',
              );
            },
            error: (error) => {
              this._showToastMessage(
                error.error?.message || 'Failed to update PDM',
                'error',
              );
            },
          });
      }
    } else {
      // Disable: delete setting
      this.settingsService
        .deleteSettingsForConnectionByType(
          this.connection.name,
          settingsTypes.FILE_OPERATION,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isPdmSettingExists = false;
            this._showToastMessage(
              'PDM file operation disabled successfully',
              'success',
            );
          },
          error: (error) => {
            // Revert toggle if delete fails
            this.isPdmEnabled = true;
            this._showToastMessage(
              error.error?.message || 'Failed to disable PDM',
              'error',
            );
          },
        });
    }
  }

  cancelPdmChange(): void {
    // Revert the toggle
    this.isPdmEnabled = !this.isPdmEnabled;
    this.showPdmConfirmPopup = false;
  }

  getSelectedEulaTitle(): string {
    const selectedEula = this.allEulaItems.find(
      (item) => item.type === this.selectedEulaType,
    );
    return selectedEula?.title || 'Default';
  }

  getTempSelectedEulaTitle(): string {
    if (
      this.tempSelectedEulaType === null ||
      this.tempSelectedEulaType === undefined
    ) {
      return 'Default';
    }

    const selectedEula = this.allEulaItems.find(
      (item) => item.type === this.tempSelectedEulaType,
    );

    return selectedEula?.title || 'Default';
  }

  // Invitation methods
  private _loadInvitations(): void {
    this.connectionService
      .getConnectionInvitations(this.connection.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (invitations) => {
          this.invitations = invitations.results;
        },
        error: () => {
          this._showToastMessage('Failed to load invitations', 'error');
        },
      });
  }

  onInviteUser(): void {
    if (!this.inviteEmail || !this.inviteEmail.trim()) {
      this._showToastMessage('Please enter a valid email address', 'error');
      return;
    }

    this.isInviting = true;

    this.connectionService
      .inviteUser(this.inviteEmail.trim(), this.connection.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (invitation) => {
          this.invitations.unshift(invitation);
          this.inviteEmail = '';
          this.showInviteUserPopup = false;
          this._showToastMessage('Invitation sent successfully', 'success');
          this.isInviting = false;
        },
        error: (error) => {
          console.error('Invitation error:', error);
          this._showToastMessage(
            error.error['detail'] || 'Failed to send invitation',
            'error',
          );
          this.isInviting = false;
        },
      });
  }

  onDeleteInvitation(invitation: UserInvitation): void {
    this.invitationToDelete = invitation;
    this.showCancelInvitationPopup = true;
  }

  confirmDeleteInvitation(): void {
    if (!this.invitationToDelete) return;

    this.userService
      .deleteUserInvitation(this.invitationToDelete.url_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.invitations = this.invitations.filter(
            (inv) => inv.url_id !== this.invitationToDelete!.url_id,
          );
          this._showToastMessage('Invitation deleted successfully', 'success');
          this.showCancelInvitationPopup = false;
          this.invitationToDelete = null;
        },
        error: (error) => {
          this._showToastMessage(
            error.error?.message || 'Failed to delete invitation',
            'error',
          );
          this.showCancelInvitationPopup = false;
          this.invitationToDelete = null;
        },
      });
  }

  getInviteLink(urlId: string): string {
    return `${window.location.origin}/self-signup/${urlId}`;
  }

  copyToClipboard(urlId: string): void {
    const link = this.getInviteLink(urlId);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.copiedInvitationId = urlId;
        setTimeout(() => {
          this.copiedInvitationId = null;
        }, 2000);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this._showToastMessage('Failed to copy link', 'error');
      });
  }

  cancelDeleteInvitation(): void {
    this.showCancelInvitationPopup = false;
    this.invitationToDelete = null;
  }

  // Email validation methods
  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  isInviteButtonDisabled(): boolean {
    return (
      this.isInviting ||
      !this.inviteEmail.trim() ||
      !this.isValidEmail(this.inviteEmail.trim())
    );
  }

  isInviteUserButtonDisabled(): boolean {
    return this.isInviting;
  }

  isDeleteConnectionAllowed(): boolean {
    return this.connection.name !== 'Username-Password-Authentication';
  }

  // CRM Roles methods
  private _loadCrmRoles(): void {
    this.crmLoading = true;
    this.crmService
      .getCRMRoles()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((roles) => {
          this.allCrmRoles = roles;
          return this.settingsService.getSettingsForConnectionByType(
            this.connection.name,
            'CRM',
          );
        }),
      )
      .subscribe({
        next: (res) => {
          this.isCRMSettingExists = true;
          const crmSettings = res.settings_value as {
            role_id: string;
            status: boolean;
          };

          // Check if a role was previously selected (even if disabled)
          if (crmSettings.role_id) {
            this.originalSelectedCrmRoleId = crmSettings.role_id; // Always store the role_id

            if (crmSettings.status) {
              // CRM is enabled with a role
              this.isCrmEnabled = true;
              this.selectedCrmRoleId = crmSettings.role_id;
              this.selectedCrmRole =
                this.allCrmRoles.find(
                  (role) => role.role_id === crmSettings.role_id,
                ) || null;
              this.isCrmRoleReadonly = true; // Role is readonly after first selection
            } else {
              // CRM is disabled but role exists - keep role for re-enabling
              this.isCrmEnabled = false;
              this.selectedCrmRoleId = null;
              this.selectedCrmRole = null;
              this.isCrmRoleReadonly = true; // Keep readonly since role was previously selected
            }
          } else {
            // No role selected yet
            this.isCrmEnabled = false;
            this.selectedCrmRoleId = null;
            this.selectedCrmRole = null;
            this.isCrmRoleReadonly = false;
            this.originalSelectedCrmRoleId = null;
          }
          this.crmLoading = false;
        },
        error: (error) => {
          if (error.status !== 404) {
            console.error('Failed to load CRM settings:', error);
            this._showToastMessage('Failed to load CRM settings', 'error');
          } else if (error.status === 404) {
            // No CRM settings found, initialize as disabled
            this.isCRMSettingExists = false;
            this.isCrmEnabled = false;
            this.selectedCrmRoleId = null;
            this.selectedCrmRole = null;
            this.isCrmRoleReadonly = false;
          }
          this.crmLoading = false;
        },
      });
  }

  onCrmToggleChange(): void {
    if (!this.isCrmEnabled) {
      // Disabling CRM - preserve the current role for re-enabling
      if (this.selectedCrmRoleId && !this.originalSelectedCrmRoleId) {
        this.originalSelectedCrmRoleId = this.selectedCrmRoleId;
      }
      this.saveCRMRole(true); // Status-only update
    } else {
      // Enabling CRM - restore the previously selected role if it exists
      if (this.originalSelectedCrmRoleId) {
        this.selectedCrmRoleId = this.originalSelectedCrmRoleId;
        this.selectedCrmRole =
          this.allCrmRoles.find(
            (role) => role.role_id === this.originalSelectedCrmRoleId,
          ) || null;
        this.isCrmRoleReadonly = true;
        this.saveCRMRole(true); // Status-only update
      } else {
        // No original role exists, wait for role selection in dropdown
        this.isCrmRoleReadonly = false;
        this.selectedCrmRoleId = null;
        this.selectedCrmRole = null;
      }
    }
  }

  onCrmRoleSelectionChange(): void {
    if (this.selectedCrmRoleId && !this.isCrmRoleReadonly) {
      // First time selection - show warning popup
      this.tempSelectedCrmRoleId = this.selectedCrmRoleId;
      this.showCrmRoleWarningPopup = true;
    }
  }

  confirmCrmRoleSelection(): void {
    if (this.tempSelectedCrmRoleId) {
      this.selectedCrmRoleId = this.tempSelectedCrmRoleId;
      this.originalSelectedCrmRoleId = this.tempSelectedCrmRoleId; // Store as original role
      this.selectedCrmRole =
        this.allCrmRoles.find(
          (role) => role.role_id === this.selectedCrmRoleId,
        ) || null;
      this.isCrmRoleReadonly = true;
      this.saveCRMRole();
    }
    this.showCrmRoleWarningPopup = false;
    this.tempSelectedCrmRoleId = null;
  }

  cancelCrmRoleSelection(): void {
    this.selectedCrmRoleId = this.selectedCrmRole?.role_id || null;
    this.showCrmRoleWarningPopup = false;
    this.tempSelectedCrmRoleId = null;
  }

  saveCRMRole(isStatusOnlyUpdate = false): void {
    // Create payload based on whether it's a status-only update or role selection
    let crmRoleData: { role_id?: string; status: boolean };

    if (isStatusOnlyUpdate) {
      // Only send status when toggling enable/disable
      crmRoleData = {
        status: this.isCrmEnabled,
      };
    } else {
      // Send both role_id and status when selecting/confirming a role
      crmRoleData = {
        role_id: this.selectedCrmRoleId || this.originalSelectedCrmRoleId || '',
        status:
          this.isCrmEnabled &&
          !!(this.selectedCrmRoleId || this.originalSelectedCrmRoleId),
      };
    }

    if (!this.isCRMSettingExists) {
      this.settingsService
        .CreateSettingsForConnectionByType(
          this.connection.name,
          settingsTypes.CRM,
          crmRoleData,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isCRMSettingExists = true;
            this._showToastMessage(
              'CRM settings saved successfully',
              'success',
            );
          },
          error: (error) => {
            this._showToastMessage(
              error.error?.message || 'Failed to save CRM settings',
              'error',
            );
          },
        });
    } else {
      this.settingsService
        .updateSettingsForConnectionByType(
          this.connection.name,
          settingsTypes.CRM,
          crmRoleData,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this._showToastMessage(
              'CRM settings updated successfully',
              'success',
            );
          },
          error: (error) => {
            this._showToastMessage(
              error.error?.message || 'Failed to update CRM settings',
              'error',
            );
          },
        });
    }
  }

  getCrmRoleDisplayName(): string {
    return this.selectedCrmRole?.role_name || 'Select a CRM role';
  }

  getTempSelectedRoleName(): string {
    return (
      this.allCrmRoles.find(
        (role) => role.role_id === this.tempSelectedCrmRoleId,
      )?.role_name || ''
    );
  }
}
